import * as Sentry from "@sentry/react";
import { logger } from "@gui/infrastructure/pass-through/logger";
import { Notifications } from "@gui/infrastructure/pass-through/notifications";
import { env } from "@gui/infrastructure/pass-through/env";
import { isAbortRejection, isDragInvariant, isInterceptorHandled } from "@gui/infrastructure/pass-through/sentryErrors";

/**
 * Initialize Sentry, configure tags, install the global unhandled-rejection handler,
 * and link Sentry to the logger. Call once at app boot before any UI renders.
 */
export function initSentry(): void {
	const isDev = env.environmentName === "development";
	const isLocalhostInUrl = window.location.hostname.search("localhost") !== -1;

	Sentry.init({
		dsn: env.sentryDsn,
		environment: env.environmentName,
		beforeSend(event, hint) {
			// A cancelled request is not a defect — see `isAbortRejection`. Dropped here rather
			// than in `ignoreErrors` so the decision is keyed on the exception's name instead of
			// on a browser-worded message.
			if (isAbortRejection(hint?.originalException)) return null;
			// The axios interceptor already ruled on this one — including "don't report it",
			// for the session-expiry 401 (see `isInterceptorHandled`). Only automatic captures
			// are dropped: the interceptor's own deliberate reports for a network failure, 403
			// or 5xx come through `captureException` with `handled: true` and must survive,
			// whether or not the flag is set on the error by the time this runs.
			if (!hint?.mechanism?.handled && isInterceptorHandled(hint?.originalException)) return null;
			// A drag-and-drop auto-scroll frame that outlived its drag — see `isDragInvariant`.
			// `@hello-pangea/dnd` already preventDefaults this one on the window `error` event;
			// Sentry's requestAnimationFrame wrapper just gets to it first. Only the automatic
			// capture is dropped, so a dnd failure that reaches an error boundary still reports.
			if (!hint?.mechanism?.handled && isDragInvariant(hint?.originalException)) return null;
			if (event.exception && event.event_id && !hint?.mechanism?.handled) {
				Sentry.showReportDialog({ eventId: event.event_id });
			}
			return event;
		},
		enableLogs: true,
		integrations: [
			// browserTracingIntegration() enables INP tracking by default in @sentry/react v10
			// (no enableInp/interactionsSampleRate option needed). Per-interaction INP attribution
			// is reported separately via initPerformanceMonitoring (web-vitals/attribution).
			Sentry.browserTracingIntegration(),
			Sentry.browserProfilingIntegration(),
			Sentry.replayIntegration(),
			Sentry.feedbackIntegration({
				colorScheme: "system",
				enableScreenshot: true,
				showBranding: false,
				autoInject: false,
			}),
		],
		tracesSampleRate: isDev ? 1.0 : 1.0, // capture 100% of transactions
		// - this should probably be smaller in production
		// - but for now I want to see what happens
		tracePropagationTargets: [
			// "localhost",
			// /^https:\/\/.*\.awsapprunner\.com/,
			env.rogoApiUrl,
		],
		profilesSampleRate: 1.0, // profile 100% of captured transactions
		replaysSessionSampleRate: isDev ? 1.0 : 0.1, // capture replays on 10% of sessions
		replaysOnErrorSampleRate: 1.0, // capture replays on 100% of errors
		enabled: (!isDev && !isLocalhostInUrl) || env.enableSentry,
	});

	Sentry.setTag("hostname", window.location.hostname);

	// Global handler for unhandled promise rejections.
	// Sentry already captures these; this adds a user-facing toast for promises that slip through without a catch.
	window.addEventListener("unhandledrejection", (event) => {
		if (isInterceptorHandled(event.reason)) return;
		// MapLibre aborts a tile's fetch on every pan; that is not something to tell the
		// operator about. Same predicate `beforeSend` uses, so the toast and the Sentry issue
		// appear and disappear together.
		if (isAbortRejection(event.reason)) return;
		Notifications.error("An unexpected error occurred.");
	});

	// @ts-expect-error @sentry/react's namespace vs shared-ts's aws-serverless-flavored Sentry type:
	// duplicate @sentry/core instances make Scope nominally incompatible (protected member) even
	// though the runtime surface Logger uses is identical. Fix belongs in @rogoag/shared-ts (type
	// `sentry` against a minimal structural interface).
	logger.sentry = Sentry;
	logger.newSession();
}
